import { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { getPlaybackUrl } from '../lib/stream-queries';
import './VideoPlayer.css';

interface Props {
  /** Media row UUID. If provided, resolves to a Stream playback URL. */
  mediaId?: string | null;
  /** Fallback raw URL (kept for backward compatibility with demo posts). */
  src?: string | null;
  poster?: string | null;
  aspect?: number; // 16/9, 9/16, 1, etc.
}

function isUuid(s: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
}

/**
 * Derive Cloudflare Stream's first-frame thumbnail from a resolved HLS/DASH
 * manifest URL, so a video shows a clean still instead of a black box before
 * playback (the team edits each clip's first frame to be a usable cover).
 * Returns null for non-Stream URLs; a 404 (e.g. signed-token videos) just
 * falls back to no poster — same black box as before, never a broken image.
 */
function streamFirstFrame(url: string): string | null {
  const m = url.match(/^(https:\/\/[^/]+\/[^/?#]+)\/manifest\/video\.(?:m3u8|mpd)/i);
  return m ? `${m[1]}/thumbnails/thumbnail.jpg?time=0s` : null;
}

export function VideoPlayer({ mediaId, src, poster, aspect = 16 / 9 }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [resolvedSrc, setResolvedSrc] = useState<string | null>(null);
  // The DB aspect_ratio is frequently null (legacy + un-probed Stream uploads),
  // which would force every video into the 16/9 fallback box and pillar/letter-
  // box portrait clips. Once the element reports its real dimensions we size the
  // box to the true ratio so the media snaps to its content with no bars.
  const [measuredAspect, setMeasuredAspect] = useState<number | null>(null);
  const effectiveAspect = measuredAspect ?? aspect;
  // Prefer an explicit cover; otherwise fall back to Stream's first-frame still
  // so the box isn't a black rectangle until the viewer taps play.
  const effectivePoster = poster ?? (resolvedSrc ? streamFirstFrame(resolvedSrc) : null);

  // Resolve playback URL. Prefer mediaId (signed/managed), fall back to src.
  useEffect(() => {
    let cancelled = false;
    async function resolve() {
      setError(null);
      setResolvedSrc(null);

      if (mediaId && isUuid(mediaId)) {
        try {
          const url = await getPlaybackUrl(mediaId);
          if (cancelled) return;
          setResolvedSrc(url);
          return;
        } catch (err: any) {
          console.warn('[VideoPlayer] playback url fetch failed', err);
          // If mediaId fails but src fallback exists, use it.
          if (src) {
            if (cancelled) return;
            setResolvedSrc(src);
            return;
          }
          if (cancelled) return;
          setError(
            err?.code === 'stream_not_configured'
              ? "This video isn't available right now."
              : 'This video is still processing. Check back in a minute.',
          );
          return;
        }
      }

      if (src) {
        setResolvedSrc(src);
        return;
      }
      setError('No video source.');
    }
    resolve();
    return () => { cancelled = true; };
  }, [mediaId, src]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !resolvedSrc) return;

    // Safari plays HLS natively.
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = resolvedSrc;
      return;
    }

    if (Hls.isSupported()) {
      const hls = new Hls({ capLevelToPlayerSize: true });
      hls.loadSource(resolvedSrc);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data.fatal) setError('This video couldn\'t load. Try refreshing.');
      });
      return () => hls.destroy();
    }

    setError('Your browser can\'t play this video.');
  }, [resolvedSrc]);


  return (
    <div
      className="video"
      style={{ aspectRatio: String(effectiveAspect), ['--video-ar' as string]: String(effectiveAspect) } as React.CSSProperties}
    >
      {!error && resolvedSrc && (
        <video
          ref={videoRef}
          controls
          playsInline
          preload="metadata"
          poster={effectivePoster ?? undefined}
          onLoadedMetadata={(e) => {
            const v = e.currentTarget;
            if (v.videoWidth > 0 && v.videoHeight > 0) {
              setMeasuredAspect(v.videoWidth / v.videoHeight);
            }
            // No poster available → nudge the first frame to paint so the box
            // isn't black (helps engines that don't render a frame at preload).
            if (!effectivePoster && v.paused && v.currentTime === 0) {
              try { v.currentTime = 0.05; } catch { /* ignore */ }
            }
          }}
        />
      )}
      {!error && !resolvedSrc && <p className="video__loading">Loading video…</p>}
      {error && <p className="video__error">{error}</p>}
    </div>
  );
}
